/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import Pendo from '@ion/core/src/analytics/Pendo';
import {generateId} from '@ion/core/src/utils/IDUtils';
import {sizeOf} from '@ion/core/src/utils/MemoryUtils';
import {removeQueryString} from '@ion/core/src/utils/QueryStringUtils';
import {
  IBeaconClient,
  MeasurementResult,
  MonitorCategory,
  MonitorResults,
  ServiceTimings
} from '../types/types';

/**
 * Defines the maximum size allowed by pendo track event payloads.
 */
const PAYLOAD_LIMIT = 512;

/**
 * Defines the service threshold against which events are to be sent; that is, if
 * the total service time exceeds this limit, the event will be emitted to Pendo,
 * otherwise, the event will be omitted.
 */
const SERVICE_THRESHOLD = 5000;

/**
 * Defines the ui threshold against which events are to be sent; that is, if
 * the total ui time exceeds this limit, the event will be emitted to Pendo,
 * otherwise, the event will be omitted.
 */
const UI_THRESHOLD = 2500;

/**
 * Defines the valid Pendo Payload Properties names (keys) that are included within
 * a Pendo Application Performance Monitoring Track Event.
 */
export enum PendoPayloadProperties {
  TOTAL_TIME = 'Total_Time',
  UI_TIME = 'UI_Time',
  SERVICE_TIME = 'Service_Time',
  MEASURED_SERVICES = 'Measured_Services',
  MEASURED_COUNT = 'Service_Count',
  DUPLICATED_SERVICES = 'Duplicate_Services',
  DUPLICATED_COUNT = 'Duplicate_Service_Count',
  EXCEPTION = 'Exception'
}

/**
 * Defines the valid Pendo Payload that can be sent to a Pendo Track Event for an
 * Application Performance Monitoring instance.
 */
export type PendoTrackEventPayload = {
  [PendoPayloadProperties.TOTAL_TIME]: string,
  [PendoPayloadProperties.SERVICE_TIME]: string,
  [PendoPayloadProperties.UI_TIME]: string,
  [PendoPayloadProperties.MEASURED_COUNT]?: string
  [PendoPayloadProperties.DUPLICATED_COUNT]?: string,
  [PendoPayloadProperties.MEASURED_SERVICES]?: string,
  [PendoPayloadProperties.DUPLICATED_SERVICES]?: string
}

/**
 * Defines the Payload types which are used for identifying associated events. Due
 * to Pendo payload constraints, each event is split into two separate events so as
 * to ensure the payload limits are not exceeded. These enums are included the event
 * names to help identify the information the event contains.
 */
export enum PendoPayloadType {
  TIMING_RESULTS = 'Timings',
  SERVICES_RESULTS = 'Services'
}

/**
 * @internal
 *
 * The Pendo `Beacon` API provides the facility for sending Application Performance
 * Monitoring events to Pendo Analytics Services.
 *
 * Each event is broken into two events, one which contains the timing results, and
 * another which contains the measured services and their timings. This is necessary
 * as Pendo Track Event Payloads have a maximum size of 512 bytes, so the event is
 * split into two separate events in order to ensure all data is captured for the
 * event.
 *
 * This API is primarily intended to be a package level instance, that is, used
 * internally by the `APM` API during initialization.
 */
class PendoBeacon implements IBeaconClient {
  /**
   * Provides the single point of entry for sending the results of an Application
   * Performance Monitoring instance to Pendo Analytics Services.
   *
   * @param   eventName The eventName which is to be sent to the external Pendo
   *          services.
   * @param   payload A `MonitorResults` instance containing the values for each
   *          performance measurement associated with the monitoring occurrence.
   * @returns `true` if the monitoring event was successfully sent, otherwise,
   *          `false`.
   */
  send(eventName: string, payload: MonitorResults, category: MonitorCategory): boolean {
    if (category !== MonitorCategory.Crash) {
      const serviceTimeExceeded = payload.serviceTime > SERVICE_THRESHOLD;
      const uiTimeExceeded      = payload.totalUITime > UI_THRESHOLD;
      if (serviceTimeExceeded || uiTimeExceeded) {
        const payloads = createPayloads(payload);
        payloads.forEach((payload: Partial<PendoTrackEventPayload>, index: number) => {
          const type  = !index ? PendoPayloadType.TIMING_RESULTS : PendoPayloadType.SERVICES_RESULTS;
          const event = createEventName(eventName, category, type);
          Pendo.track(event, payload);
        });
        return true;
      }
    } else {
      Pendo.track(createEventName(eventName, category), {
        [PendoPayloadProperties.EXCEPTION] : payload.error?.message || 'N/A'
      });
      return true;
    }
    return false;
  }
}

/**
 * Provides a utility which creates a standardized event name based on the given
 * parameters.
 */
const createEventName = (eventName: string, category: MonitorCategory, type?: PendoPayloadType): string => {
  const name = `APM - ${category} ${eventName}`;
  if (category !== MonitorCategory.Crash) {
    return `${name} (${type})`;
  }
  return name;
};

/**
 * Provides a utility which converts a `MonitorResults` instance to a Pendo Track
 * Event Payload based on a specific format.
 *
 * By default, Pendo Track Event Payloads can not exceed `512` bytes, thus, if the
 * generated payload exceeds the maximum size, the measured services array will be
 * removed from the payload (as it's value is likely the cause of the increased size).
 *
 * @param   results The `MonitorResults` which are to be transformed into Pendo Track
 *          event properties and validated.
 * @param   minimize When `true` removes service path prefixed (e.g. `/connect/services')
 *          in order to reduce the size of the payload.
 * @returns The resulting `PendoTrackEventPayload` based on the provided Performance
 *          Monitor results.
 */
export const createPayloads = (results: MonitorResults, minimize = true): Partial<PendoTrackEventPayload>[] => {
  const {
    totalTime,
    totalUITime,
    serviceTime,
    duplications,
    measuredServices,
    serviceTimings
  } = results;
  const timings = verifySize({
    [PendoPayloadProperties.TOTAL_TIME]       : `${verifyTime(totalTime)}`,
    [PendoPayloadProperties.UI_TIME]          : `${verifyTime(totalUITime)}`,
    [PendoPayloadProperties.SERVICE_TIME]     : `${verifyTime(serviceTime)}`,
    [PendoPayloadProperties.MEASURED_COUNT]   : `${measuredServices?.length ?? 0}`,
    [PendoPayloadProperties.DUPLICATED_COUNT] : `${duplications?.length ?? 0}`
  }, true);
  const services = verifySize({
    [PendoPayloadProperties.MEASURED_SERVICES]   : formatServiceTimings(serviceTimings, minimize),
    [PendoPayloadProperties.DUPLICATED_SERVICES] : `${duplications?.toString()}`
  }, false);
  return [timings, services];
};

/**
 * Provides a utility which ensures the given payload does not exceed the max
 * payload of 512 bytes minus 128bytes of additional padding (384bytes) in total
 * enforced by the Pendo Track Event. The additional padding is added to ensure
 * Pendo does not fail to add the payload which can occur in certain instances
 * even if the payload is 512bytes or less.
 *
 * If the payload exceeds the maximum size, first, the duplicated services will
 * be removed from the payload (if any), followed by the measured services array
 * (as their values are likely the cause of the increased size), in which case,
 * the value will be set to the number of services, rather than an array of
 * service urls.
 *
 * Note, while the maximum size of a track event payload in Pendo is documented
 * as being 512bytes, in viewing Pendo track events of payloads less than the
 * maximum, Pendo displays an "event properties JSON too large" error, therefore
 * to ensure the payload is captured, we set the max size to one half the total
 * max size allows as a work-around.
 */
const verifySize = (
  payload: Partial<PendoTrackEventPayload>,
  isCounts = false
): Partial<PendoTrackEventPayload> => {
  if (sizeOf(payload) >= (PAYLOAD_LIMIT - 128)) {
    if (!isCounts) {
      delete payload[PendoPayloadProperties.MEASURED_SERVICES];
    }
  }
  return payload as Partial<PendoTrackEventPayload>;
};

/**
 * Provides a utility which transforms the event's `serviceTimings` instance to a string
 * for use within Pendo's track event API.
 */
export const formatServiceTimings = (serviceTimings: ServiceTimings, minimize = true): string => {
  let results = '';
  Object.entries(serviceTimings).map(([service, result]: [string, Partial<MeasurementResult>[]]) => {
    const name = minimize ? minimizeServiceURL(service) : service;
    results += `${name}: ${result.map(timing => verifyTime(timing.duration)).toString()}\n`;
  });
  return results;
};

/**
 * Provides a utility which removes CalAmp service url prefixes (e.g.'/connect/services')
 * as well as unique identifiers such as entity ids, application keys, and jasper tokens.
 * In addition, the query string (if present) will also be removed. This allows for limiting
 * the number of potential permutations of property values in pendo as well as minimizing
 * the size of the string in order to reduce the size of the payload values and limit the
 * cardinality of property values.
 *
 * @param   service the service url which is to be minified.
 * @returns the minimized version of the provided service url.
 */
export const minimizeServiceURL = (service: string): string => {
  const uuidPattern  = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const idPattern    = /^\d+$/;
  const url  = removeQueryString(removeServicePaths(service));
  const path = url.split('/').filter((segment: string) => (
    !segment.match(idPattern) && !segment.match(uuidPattern)
  )).join('/');
  return path;
};

/**
 * Provides a utility which removes service paths, typically those within the URI root
 * paths, as well as additional unnecessary paths within the measured services in order
 * to minimize the measured URL.
 */
const removeServicePaths = (service: string): string => {
  const reporting = '/reporting';
  const register  = 'registertoken';
  if (service.indexOf(register) > -1) {
    service = service.substring(0, `${reporting}/${register}`.length);
  }
  const serviceRoots = [
    '/connect/services',
    '/device-services',
    '/alert-engine/services',
    '/api/mfg/v1',
    reporting
  ];
  for (const startingPath of serviceRoots) {
    if (service.startsWith(startingPath)) {
      return service.substring(startingPath.length);
    }
  }
  return service;
};
/**
 * Provides a utility which verifies the given time value, converting the time
 * from milliseconds to seconds (fixed to 2 decimals) as a string, otherwise,
 * if the value is nil or NaN, returns 'N/A'.
 *
 * @param   val the measured value in milliseconds which is to be converted
 *          to a value for display in Pendo.
 * @returns the given time value in seconds (if valid), otherwise, 'N/A'.
 */
const verifyTime = (val: number): string => (
  val && !isNaN(val) ? `${Math.round((val / 1000))}` : 'N/A'
);

export default new PendoBeacon();
